// src/features/setup/SetupWizard.jsx
import React, { useEffect, useMemo, useState } from "react";


/**
 * SetupWizard.jsx — v10 (2025-11-11)
 * - Prefill sessione via /api/autoconfig/load-session.php
 * - Bottone "Esci e continua dopo"
 * - Redirect automatico alla dashboard alla fine
 * - Rail sinistro con step verdi quando completati
 * - Ordine: 1 RS → 2 UO → 3 Prodotti → 8 Processi → 4 Igiene → 5 PRP → 6 Ruoli → 7 Servizi
 */

/* ---------- UI atoms ---------- */
const Label = ({ children, className = "" }) => (
  <label className={`text-sm font-medium text-gray-800 ${className}`}>{children}</label>
);
const Helper = ({ children }) => <p className="text-xs text-gray-500 mt-1">{children}</p>;
const SectionCard = ({ title, children, className = "" }) => (
  <div className={`bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-6 ${className}`}>
    {title ? <h3 className="text-base md:text-lg font-semibold mb-3">{title}</h3> : null}
    {children}
  </div>
);
const BoolButtons = ({ label, value, onChange }) => (
  <div className="mb-2">
    <Label>{label}</Label>
    <div className="flex gap-2 mt-2">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`px-3 py-1.5 rounded border ${value === true ? "bg-emerald-600 text-white border-emerald-600" : "bg-white"}`}
      >
        Sì
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`px-3 py-1.5 rounded border ${value === false ? "bg-rose-600 text-white border-rose-600" : "bg-white"}`}
      >
        No
      </button>
    </div>
  </div>
);
const TogglePill = ({ active, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full border ${active ? "bg-blue-600 text-white border-blue-600" : "bg-white"}`}
  >
    {children}
  </button>
);
const CheckItem = ({ checked, label, onChange }) => (
  <label className="flex items-start gap-2">
    <input type="checkbox" className="mt-1" checked={checked} onChange={e => onChange(e.target.checked)} />
    <span className="text-sm">{label}</span>
  </label>
);
const CheckList = ({ items, values, setValues }) => {
  const toggle = (key, on) => {
    const set = new Set(values || []);
    if (on) set.add(key); else set.delete(key);
    setValues(Array.from(set));
  };
  return (
    <div className="grid md:grid-cols-2 gap-2">
      {items.map(it => (
        <CheckItem
          key={it.key}
          label={it.label}
          checked={(values || []).includes(it.key)}
          onChange={(on) => toggle(it.key, on)}
        />
      ))}
    </div>
  );
};

/* ---------- API helper ---------- */
async function saveStepApi(sessionId, stepSlug, fields, extra = {}) {
  const res = await fetch("/api/autoconfig/save-step.php", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ sessione_id: sessionId, step: stepSlug, fields, ...extra }),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status}${txt ? `: ${txt}` : ""}`);
  }
  return res.json(); // { ok:true, last_step:n }
}

/* ---------- Steps meta (ordine e label) ---------- */
const STEP_META = [
  { id: 1, slug: "identita", label: "Ragione sociale", minutes: 1 },
  { id: 2, slug: "unita_operativa", label: "Unità Operativa", minutes: 3 },
  { id: 3, slug: "prodotti_gestiti", label: "Prodotti Gestiti", minutes: 3 },
  { id: 8, slug: "processi_lavorazione", label: "Processi di lavorazione", minutes: 3 },
  { id: 4, slug: "igiene_sanificazione", label: "Igiene & Sanificazione", minutes: 2 },
  { id: 5, slug: "igiene_strutturale_impianti", label: "Igiene Strutturale & Impianti", minutes: 4 },
  { id: 6, slug: "ruoli_responsabilita_formazione", label: "Ruoli & Formazione", minutes: 2 },
  { id: 7, slug: "servizi_esterni", label: "Servizi Esterni", minutes: 3 },
];

export default function SetupWizard({ onCompleted, mode }) {
  /* ---------- Sessione & stato globale ---------- */
  const [sessionId, setSessionId] = useState(null);
  const [lastStep, setLastStep] = useState(1);
  const [currentUoId, setCurrentUoId] = useState(null);

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [bootError, setBootError] = useState(null);

  // Schermata iniziale di benvenuto (Pagina 0)
  const [showIntro, setShowIntro] = useState(true);


  /* ---------- STEP 1: Ragione Sociale ---------- */
  const [utenteRuolo, setUtenteRuolo] = useState("proprietario"); // 'proprietario' | 'operatore'
  const [ragioneSociale, setRagioneSociale] = useState("");
  const [piva, setPiva] = useState("");
  const [indirizzoSede, setIndirizzoSede] = useState("");
  const [capSede, setCapSede] = useState("");
  const [comuneSede, setComuneSede] = useState("");
  const [provinciaSede, setProvinciaSede] = useState("");
  const [codiceUnivoco, setCodiceUnivoco] = useState("");

  const [refNomeCognome, setRefNomeCognome] = useState("");
  const [refCell, setRefCell] = useState("");
  const [refEmail, setRefEmail] = useState("");

  // operatore
  const [opNome, setOpNome] = useState("");
  const [opCognome, setOpCognome] = useState("");
  const [opRuolo, setOpRuolo] = useState("");
  const [opCF, setOpCF] = useState("");
  const [opCell, setOpCell] = useState("");
  const [opEmail, setOpEmail] = useState("");

  // consensi
  const [consensoPrivacy, setConsensoPrivacy] = useState(false);
  const [consensoMarketing, setConsensoMarketing] = useState(false);

  /* ---------- STEP 2: Unità Operativa ---------- */
  const [uoNome, setUoNome] = useState("");
  const [uoIndirizzo, setUoIndirizzo] = useState("");
  const [uoCap, setUoCap] = useState("");
  const [uoCitta, setUoCitta] = useState("");
  const [uoPaese, setUoPaese] = useState("");
  const [uoCopiaDaAzienda, setUoCopiaDaAzienda] = useState(true);
  const [uoAddetti, setUoAddetti] = useState("");
  const [uoMQProduzione, setUoMQProduzione] = useState("");
  const [uoMQSomministrazione, setUoMQSomministrazione] = useState("");

  const SERVIZI_OPTS = [
    { key: "produzione", label: "Produzione" },
    { key: "tavolo", label: "Servizio al Tavolo" },
    { key: "asporto_delivery", label: "Asporto/Delivery" },
    { key: "banco", label: "Servizio al Banco" },
    { key: "catering", label: "Catering" },
    { key: "ciclo_freddo", label: "Ciclo Freddo" },
    { key: "ciclo_caldo", label: "Ciclo Caldo" },
    { key: "ciclo_misto", label: "Ciclo Misto" },
  ];
  const [uoServizi, setUoServizi] = useState([]);

  const AREE_PRODUZIONE = [
    { key: "cucina_calda", label: "Cucina Calda (Cottura/Rigenerazione)" },
    { key: "cucina_fredda", label: "Cucina Fredda (Garde Manger)" },
    { key: "lavaggio_stoviglie", label: "Lavaggio Stoviglie/Batterie" },
    { key: "pizzeria", label: "Pizzeria / Panificazione" },
    { key: "gelateria", label: "Gelateria (Laboratorio Gelato)" },
    { key: "pasticceria", label: "Pasticceria (Laboratorio)" },
    { key: "macelleria", label: "Macelleria / Lavorazione Carni" },
    { key: "pescheria", label: "Pescheria / Lavorazione Ittica" },
  ];
  const [uoAreeProd, setUoAreeProd] = useState([]);

  const AREE_SERVIZIO = [
    { key: "sala_tavoli", label: "Sala / Somministrazione a Tavola" },
    { key: "banco_bar", label: "Banco Bar / Caffetteria" },
    { key: "buffet_self", label: "Buffet / Self-Service" },
    { key: "banco_vendita", label: "Banco di Vendita (Asporto/Gastronomia)" },
    { key: "delivery_pack", label: "Delivery / Confezionamento Asporto" },
  ];
  const [uoAreeServ, setUoAreeServ] = useState([]);

  const [respDirettore, setRespDirettore] = useState("");
  const [respApplicazione, setRespApplicazione] = useState("");
  const [respCucina, setRespCucina] = useState("");

  /* ---------- STEP 3: Prodotti Gestiti ---------- */
  const CAT_ALTO_RISCHIO = [
    { key: "carni_fresche", label: "Carni Fresche (Bovine, Suine, Ovine)" },
    { key: "pollame", label: "Pollame (Pollo, Tacchino, ecc.)" },
    { key: "pesce_fresco", label: "Pesce/Ittici Freschi (Sfilettatura in loco)" },
    { key: "molluschi_bivalvi", label: "Molluschi Bivalvi Vivi" },
    { key: "latte_derivati_crudi", label: "Latte e Derivati Crudi/Freschissimi" },
    { key: "uova_ovoprodotti", label: "Uova e Ovoprodotti Freschi" },
    { key: "vegetali_iv_gamma", label: "Vegetali di IV Gamma" },
    { key: "allergeni_critici", label: "Allergeni Critici (>5 allergeni gestiti)" },
  ];
  const [catAltoRischio, setCatAltoRischio] = useState([]);

  const STATI_CONSERVAZIONE = [
    { key: "refrigerati", label: "Refrigerati (≤4°C o specifica di legge)" },
    { key: "congelati", label: "Congelati/Surgelati (-18°C)" },
    { key: "secco_scaffale", label: "Secco - stoccato a scaffale" },
    { key: "caldi_mantenimento", label: "Caldi / Mantenimento (>60/65°C)" },
  ];
  const [statiConservazione, setStatiConservazione] = useState([]);

  const PROD_SPEC = [
    { key: "sottovuoto_atm", label: "Tecniche Sottovuoto / ATM" },
    { key: "cook_chill", label: "Abbattimento Rapido / Cook & Chill" },
    { key: "hot_holding", label: "Rigenerazione / Mantenimento in Caldo (Hot Holding)" },
    { key: "limiti_critici_cottura", label: "Definizione Limiti Critici di Cottura (carne/pollame)" },
  ];
  const [produzioniSpeciali, setProduzioniSpeciali] = useState([]);

  const PROD_ALTO_PERICOLO = [
    { key: "cbt_sous_vide", label: "Cottura Sicura a Bassa T (CBT/Sous Vide)" },
    { key: "uova_crude", label: "Uova fresche a crudo (tiramisù/maionese...)" },
    { key: "conto_terzi_animale_50", label: "Produzione conto terzi >50% origine animale" },
    { key: "alto_rischio_pesce_crudo", label: "Trasformazione pesce crudo (Anisakis)" },
    { key: "mp_crude_preabbattute", label: "Materie prime crude pre-abbattute" },
  ];
  const [prodAltoPericolo, setProdAltoPericolo] = useState([]);

  /* ---------- STEP 8: Processi (state) ---------- */
  const [allergeniGestioneOn, setAllergeniGestioneOn] = useState(true);
  const [menuStampatoDigitale, setMenuStampatoDigitale] = useState(false);
  const [registroIngredientiCartaceo, setRegistroIngredientiCartaceo] = useState(false);
  const [venditeSfusePreincartate, setVenditeSfusePreincartate] = useState(false);
  const [avvisoTracce, setAvvisoTracce] = useState(null);

  const [areaDedicataGF, setAreaDedicataGF] = useState(null);
  const [attrezzatureDedicate, setAttrezzatureDedicate] = useState(null);
  const [gestioneIngrAllergenici, setGestioneIngrAllergenici] = useState(null);
  const [formazioneSpecAllergeni, setFormazioneSpecAllergeni] = useState(null);

  const [congelaInternamente, setCongelaInternamente] = useState(null);
  const [etichetteTracciabilita, setEtichetteTracciabilita] = useState(null);
  const [destinazioneCongelato, setDestinazioneCongelato] = useState("");
  const [usaCongelatiOrigine, setUsaCongelatiOrigine] = useState(null);

  const SEMI_OPTS = [
    { key: "pasta_pane", label: "Pasta e Pane" },
    { key: "gelati_sorbetti", label: "Gelati e Sorbetti (+ registro maturazione/abbattimento)" },
    { key: "dolci_pasticceria", label: "Dolci e Preparati di Pasticceria" },
    { key: "salse_maionesi_vinaigrette", label: "Salse/Maionese/Vinaigrette (ex novo)" },
    { key: "arrosti_grossa_pezzatura", label: "Arrosti / Grandi Pezzature" },
    { key: "brodi_fondi_zuppe", label: "Brodi / Fondi / Zuppe" },
    { key: "preparazioni_uova_crude", label: "Preparazioni a Base di Uova non Cotte" },
  ];
  const [semiList, setSemiList] = useState([]);

  const FILTRI_OPTS = [
    { key: "macchina_ghiaccio", label: "Macchina del Ghiaccio" },
    { key: "lavastoviglie", label: "Lavastoviglie / Lavaoggetti" },
    { key: "bollitori_bras", label: "Bollitori / Brasiere" },
    { key: "linea_prep_alimenti", label: "Linea di Preparazione Alimenti" },
    { key: "erogatori_bibite", label: "Erogatori Acqua da Bere" },
  ];
  const [filtriPresenti, setFiltriPresenti] = useState(null);
  const [filtriAree, setFiltriAree] = useState([]);

  const [usaAI, setUsaAI] = useState(false);
  const [descrizioneLibera, setDescrizioneLibera] = useState("");
  const [profiloSuggerito, setProfiloSuggerito] = useState("");

  /* ---------- STEP 4: Igiene & Sanificazione ---------- */
  const [separazioneVestizione, setSeparazioneVestizione] = useState(null);

  /* ---------- STEP 5: Igiene Strutturale & Impianti ---------- */
  const [rubinetteriaNonManuale, setRubinetteriaNonManuale] = useState(null);
  const [illuminazioneProtetta, setIlluminazioneProtetta] = useState(null);
  const [controlloInfestanti, setControlloInfestanti] = useState(""); // "attivo"|"passivo"|""
  const [sigillaturaStrutturale, setSigillaturaStrutturale] = useState(null);

  /* ---------- STEP 6: Ruoli & Formazione ---------- */
  const [haccpRespNome, setHaccpRespNome] = useState("");
  const [haccpRespCognome, setHaccpRespCognome] = useState("");
  const [haccpRespRuolo, setHaccpRespRuolo] = useState("");
  const [haccpRespCell, setHaccpRespCell] = useState("");
  const [haccpRespEmail, setHaccpRespEmail] = useState("");
  const [haccpRespIsReferente, setHaccpRespIsReferente] = useState(false);
  const [formazionePersonaleOk, setFormazionePersonaleOk] = useState(null);
  const [usaAcquaPozzo, setUsaAcquaPozzo] = useState(null);
  const [ulssAutorizzazione, setUlssAutorizzazione] = useState("");

  /* ---------- STEP 7: Servizi Esterni ---------- */
  const [hasDittaInf, setHasDittaInf] = useState(false);
  const [infSocieta, setInfSocieta] = useState("");
  const [infSede, setInfSede] = useState("");
  const [infPiva, setInfPiva] = useState("");
  const [infTel, setInfTel] = useState("");
  const [infEmail, setInfEmail] = useState("");
  const [infRefNome, setInfRefNome] = useState("");
  const [infRefCognome, setInfRefCognome] = useState("");
  const [infRefCell, setInfRefCell] = useState("");
  const [infTelPronto, setInfTelPronto] = useState("");

  const [olSocieta, setOlSocieta] = useState("");
  const [olSede, setOlSede] = useState("");
  const [olPiva, setOlPiva] = useState("");
  const [olTel, setOlTel] = useState("");
  const [olEmail, setOlEmail] = useState("");
  const [olRefNome, setOlRefNome] = useState("");
  const [olRefCognome, setOlRefCognome] = useState("");
  const [olRefCell, setOlRefCell] = useState("");
  const [olTelPronto, setOlTelPronto] = useState("");

  /* ---------- Avvio sessione + Prefill ---------- */
  useEffect(() => {
    (async () => {
      try {
        const startUrl =
          mode === "edit"
            ? "/api/autoconfig/start.php?mode=edit"
            : "/api/autoconfig/start.php";

        const r = await fetch(startUrl, { credentials: "include" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!j?.sessione_id) throw new Error("start: risposta non valida");
        setSessionId(j.sessione_id);
        setLastStep(j.last_step || 1);
        setStep(j.last_step || 1);

        // Prefill da DB
        const r2 = await fetch(`/api/autoconfig/load-session.php?sessione_id=${j.sessione_id}`, { credentials: "include" });
        if (r2.ok) {
          const data = await r2.json();
          if (data?.ok && data.answers) {
            const A = data.answers;

            // Step 1
            const S1 = A['identita'] || {};
            if (S1.utente_ruolo) setUtenteRuolo(S1.utente_ruolo);
            if (S1.ragione_sociale) setRagioneSociale(S1.ragione_sociale);
            if (S1.partita_iva) setPiva(S1.partita_iva);
            if (S1.indirizzo_sede) setIndirizzoSede(S1.indirizzo_sede);
            if (S1.cap) setCapSede(S1.cap);
            if (S1.comune) setComuneSede(S1.comune);
            if (S1.provincia) setProvinciaSede(S1.provincia);
            if (S1.codice_univoco) setCodiceUnivoco(S1.codice_univoco);
            if (S1.referente_nome_cognome) setRefNomeCognome(S1.referente_nome_cognome);
            if (S1.referente_cell) setRefCell(S1.referente_cell);
            if (S1.referente_email) setRefEmail(S1.referente_email);
            if (typeof S1.consenso_privacy !== "undefined") setConsensoPrivacy(!!S1.consenso_privacy);
            if (typeof S1.consenso_marketing !== "undefined") setConsensoMarketing(!!S1.consenso_marketing);
            if (S1.op_nome) setOpNome(S1.op_nome);
            if (S1.op_cognome) setOpCognome(S1.op_cognome);
            if (S1.op_ruolo) setOpRuolo(S1.op_ruolo);
            if (S1.op_codice_fiscale) setOpCF(S1.op_codice_fiscale);
            if (S1.op_cell) setOpCell(S1.op_cell);
            if (S1.op_email) setOpEmail(S1.op_email);

            // Step 2
            const S2 = A['unita_operativa'] || {};
            if (S2.nome_unita_operativa) setUoNome(S2.nome_unita_operativa);
            if (S2.indirizzo) setUoIndirizzo(S2.indirizzo);
            if (S2.cap) setUoCap(S2.cap);
            if (S2.citta) setUoCitta(S2.citta);
            if (S2.paese) setUoPaese(S2.paese);
            if (typeof S2.usa_dati_rs !== "undefined") setUoCopiaDaAzienda(!!S2.usa_dati_rs);
            if (typeof S2.n_addetti !== "undefined") setUoAddetti(String(S2.n_addetti ?? ""));
            if (typeof S2.mq_produzione !== "undefined") setUoMQProduzione(String(S2.mq_produzione ?? ""));
            if (typeof S2.mq_somministrazione !== "undefined") setUoMQSomministrazione(String(S2.mq_somministrazione ?? ""));
            if (Array.isArray(S2.tipi_servizio)) setUoServizi(S2.tipi_servizio);
            if (Array.isArray(S2.aree_produzione)) setUoAreeProd(S2.aree_produzione);
            if (Array.isArray(S2.aree_servizio)) setUoAreeServ(S2.aree_servizio);
            if (S2.resp_direttore_autocontrollo) setRespDirettore(S2.resp_direttore_autocontrollo);
            if (S2.resp_applicazione_autocontrollo) setRespApplicazione(S2.resp_applicazione_autocontrollo);
            if (S2.resp_cucina) setRespCucina(S2.resp_cucina);

            // Step 3
            const S3 = A['prodotti_gestiti'] || {};
            if (Array.isArray(S3.categorie_alto_rischio)) setCatAltoRischio(S3.categorie_alto_rischio);
            if (Array.isArray(S3.stati_conservazione)) setStatiConservazione(S3.stati_conservazione);
            if (Array.isArray(S3.produzioni_speciali)) setProduzioniSpeciali(S3.produzioni_speciali);
            if (Array.isArray(S3.produzioni_alto_pericolo)) setProdAltoPericolo(S3.produzioni_alto_pericolo);

            // Step 8
            const S8 = A['processi_lavorazione'] || {};
            if (typeof S8.allergeni_gestione_on !== "undefined") setAllergeniGestioneOn(!!S8.allergeni_gestione_on);
            if (typeof S8.menu_stampato_digitale !== "undefined") setMenuStampatoDigitale(!!S8.menu_stampato_digitale);
            if (typeof S8.registro_ingredienti_cartaceo !== "undefined") setRegistroIngredientiCartaceo(!!S8.registro_ingredienti_cartaceo);
            if (typeof S8.vendite_sfuse_preincartate !== "undefined") setVenditeSfusePreincartate(!!S8.venditeSfusePreincartate);
            if (typeof S8.avviso_tracce !== "undefined") setAvvisoTracce(!!S8.avviso_tracce);
            if (typeof S8.area_dedicata_gf !== "undefined") setAreaDedicataGF(!!S8.area_dedicata_gf);
            if (typeof S8.attrezzature_dedicate !== "undefined") setAttrezzatureDedicate(!!S8.attrezzature_dedicate);
            if (typeof S8.gestione_ingr_allergenici !== "undefined") setGestioneIngrAllergenici(!!S8.gestione_ingr_allergenici);
            if (typeof S8.formazione_specifica_allergeni !== "undefined") setFormazioneSpecAllergeni(!!S8.formazione_specifica_allergeni);
            if (typeof S8.congela_internamente !== "undefined") setCongelaInternamente(!!S8.congela_internamente);
            if (typeof S8.etichette_tracciabilita !== "undefined") setEtichetteTracciabilita(!!S8.etichette_tracciabilita);
            if (typeof S8.destinazione_congelato === "string") setDestinazioneCongelato(S8.destinazione_congelato);
            if (typeof S8.usa_congelati_origine !== "undefined") setUsaCongelatiOrigine(!!S8.usa_congelati_origine);
            if (Array.isArray(S8.lista_semilavorati)) setSemiList(S8.lista_semilavorati);
            if (typeof S8.filtri_presenti !== "undefined") setFiltriPresenti(!!S8.filtri_presenti);
            if (Array.isArray(S8.filtri_aree)) setFiltriAree(S8.filtri_aree);
            if (typeof S8.usa_ai_descrizione !== "undefined") setUsaAI(!!S8.usa_ai_descrizione);
            if (typeof S8.descrizione_libera === "string") setDescrizioneLibera(S8.descrizione_libera);
            if (typeof S8.profilo_suggerito === "string") setProfiloSuggerito(S8.profilo_suggerito);

            // Step 4
            const S4 = A['igiene_sanificazione'] || {};
            if (typeof S4.separazione_vestizione !== "undefined") setSeparazioneVestizione(!!S4.separazione_vestizione);

            // Step 5
            const S5 = A['igiene_strutturale_impianti'] || {};
            if (typeof S5.rubinetteria_non_manuale !== "undefined") setRubinetteriaNonManuale(!!S5.rubinetteria_non_manuale);
            if (typeof S5.illuminazione_protetta !== "undefined") setIlluminazioneProtetta(!!S5.illuminazione_protetta);
            if (typeof S5.controllo_infestanti === "string") setControlloInfestanti(S5.controllo_infestanti);
            if (typeof S5.sigillatura_strutturale !== "undefined") setSigillaturaStrutturale(!!S5.sigillatura_strutturale);

            // Step 6
            const S6 = A['ruoli_responsabilita_formazione'] || {};
            if (S6.haccp_resp_nome) setHaccpRespNome(S6.haccp_resp_nome);
            if (S6.haccp_resp_cognome) setHaccpRespCognome(S6.haccp_resp_cognome);
            if (S6.haccp_resp_ruolo) setHaccpRespRuolo(S6.haccp_resp_ruolo);
            if (S6.haccp_resp_cell) setHaccpRespCell(S6.haccp_resp_cell);
            if (S6.haccp_resp_email) setHaccpRespEmail(S6.haccp_resp_email);
            if (typeof S6.haccp_resp_is_referente !== "undefined") setHaccpRespIsReferente(!!S6.haccp_resp_is_referente);
            if (typeof S6.formazione_personale_ok !== "undefined") setFormazionePersonaleOk(!!S6.formazione_personale_ok);
            if (typeof S6.usa_acqua_pozzo !== "undefined") setUsaAcquaPozzo(!!S6.usa_acqua_pozzo);
            if (typeof S6.ulss_autorizzazione === "string") setUlssAutorizzazione(S6.ulss_autorizzazione);

            // Step 7
            const S7 = A['servizi_esterni'] || {};
            if (typeof S7.has_ditta_infestanti !== "undefined") setHasDittaInf(!!S7.has_ditta_infestanti);
            if (S7.inf_societa) setInfSocieta(S7.inf_societa);
            if (S7.inf_sede) setInfSede(S7.inf_sede);
            if (S7.inf_piva) setInfPiva(S7.inf_piva);
            if (S7.inf_tel) setInfTel(S7.inf_tel);
            if (S7.inf_email) setInfEmail(S7.inf_email);
            if (S7.inf_ref_nome) setInfRefNome(S7.inf_ref_nome);
            if (S7.inf_ref_cognome) setInfRefCognome(S7.inf_ref_cognome);
            if (S7.inf_ref_cell) setInfRefCell(S7.inf_ref_cell);
            if (S7.inf_tel_pronto) setInfTelPronto(S7.inf_tel_pronto);

            if (S7.ol_societa) setOlSocieta(S7.ol_societa);
            if (S7.ol_sede) setOlSede(S7.ol_sede);
            if (S7.ol_piva) setOlPiva(S7.ol_piva);
            if (S7.ol_tel) setOlTel(S7.ol_tel);
            if (S7.ol_email) setOlEmail(S7.ol_email);
            if (S7.ol_ref_nome) setOlRefNome(S7.ol_ref_nome);
            if (S7.ol_ref_cognome) setOlRefCognome(S7.ol_ref_cognome);
            if (S7.ol_ref_cell) setOlRefCell(S7.ol_ref_cell);
            if (S7.ol_tel_pronto) setOlTelPronto(S7.ol_tel_pronto);
          }
        }
      } catch (e) {
        console.error(e);
        setBootError("Impossibile iniziare la sessione di configurazione.");
      }
    })();
  }, []);

  /* ---------- Completed steps set ---------- */
  const done = useMemo(() => {
    const s = new Set();
    STEP_META.forEach(m => { if (m.id <= lastStep) s.add(m.id); });
    return s;
  }, [lastStep]);

  /* ---------- Handlers step 1–3 ---------- */
  const handleNext1 = async () => {
    if (!sessionId) return;
    setSaving(true); setSaveError(null);
    try {
      const fields = [
        { key: "utente_ruolo", type: "select", value: utenteRuolo },
        { key: "ragione_sociale", type: "text", value: ragioneSociale.trim() },
        { key: "partita_iva", type: "text", value: piva.trim() },
        { key: "indirizzo_sede", type: "text", value: indirizzoSede.trim() },
        { key: "cap", type: "text", value: capSede.trim() },
        { key: "comune", type: "text", value: comuneSede.trim() },
        { key: "provincia", type: "text", value: provinciaSede.trim() },
        { key: "codice_univoco", type: "text", value: codiceUnivoco.trim() },
        { key: "referente_nome_cognome", type: "text", value: refNomeCognome.trim() },
        { key: "referente_cell", type: "text", value: refCell.trim() },
        { key: "referente_email", type: "text", value: refEmail.trim() },
        { key: "consenso_privacy", type: "bool", value: !!consensoPrivacy },
        { key: "consenso_marketing", type: "bool", value: !!consensoMarketing },
      ];
      if (utenteRuolo === "operatore") {
        fields.push(
          { key: "op_nome", type: "text", value: opNome.trim() },
          { key: "op_cognome", type: "text", value: opCognome.trim() },
          { key: "op_ruolo", type: "text", value: opRuolo.trim() },
          { key: "op_codice_fiscale", type: "text", value: opCF.trim() },
          { key: "op_cell", type: "text", value: opCell.trim() },
          { key: "op_email", type: "text", value: opEmail.trim() },
        );
      }
      const out = await saveStepApi(sessionId, "identita", fields);
      setLastStep(out?.last_step || lastStep);
      setStep(2);
    } catch (e) {
      console.error(e); setSaveError("Errore salvataggio Step 1.");
    } finally { setSaving(false); }
  };

  const handleNext2 = async () => {
    if (!sessionId) return;
    setSaving(true); setSaveError(null);
    try {
      const out = await saveStepApi(
        sessionId,
        "unita_operativa",
        [
          { key: "nome_unita_operativa", type: "text", value: uoNome.trim() },
          { key: "indirizzo", type: "text", value: uoIndirizzo.trim() },
          { key: "cap", type: "text", value: uoCap.trim() },
          { key: "citta", type: "text", value: uoCitta.trim() },
          { key: "paese", type: "text", value: uoPaese.trim() },
          { key: "usa_dati_rs", type: "bool", value: !!uoCopiaDaAzienda },
          { key: "n_addetti", type: "number", value: uoAddetti === "" ? "" : Number(uoAddetti) },
          { key: "mq_produzione", type: "number", value: uoMQProduzione === "" ? "" : Number(uoMQProduzione) },
          { key: "mq_somministrazione", type: "number", value: uoMQSomministrazione === "" ? "" : Number(uoMQSomministrazione) },
          { key: "tipi_servizio", type: "multiselect", value: uoServizi },
          { key: "aree_produzione", type: "multiselect", value: uoAreeProd },
          { key: "aree_servizio", type: "multiselect", value: uoAreeServ },
          { key: "resp_direttore_autocontrollo", type: "text", value: respDirettore.trim() },
          { key: "resp_applicazione_autocontrollo", type: "text", value: respApplicazione.trim() },
          { key: "resp_cucina", type: "text", value: respCucina.trim() },
        ]
      );
      setCurrentUoId((prev) => prev || 1);
      setLastStep(out?.last_step || lastStep);
      setStep(3);
    } catch (e) {
      console.error(e); setSaveError("Errore salvataggio Step 2.");
    } finally { setSaving(false); }
  };

  const handleNext3 = async () => {
    if (!sessionId) return;
    setSaving(true); setSaveError(null);
    try {
      const out = await saveStepApi(
        sessionId,
        "prodotti_gestiti",
        [
          { key: "categorie_alto_rischio", type: "multiselect", value: catAltoRischio },
          { key: "stati_conservazione", type: "multiselect", value: statiConservazione },
          { key: "produzioni_speciali", type: "multiselect", value: produzioniSpeciali },
          { key: "produzioni_alto_pericolo", type: "multiselect", value: prodAltoPericolo },
        ],
        currentUoId ? { uo_id: currentUoId } : {}
      );
      setLastStep(out?.last_step || lastStep);
      setStep(8); // dopo il 3 si va ai Processi
    } catch (e) {
      console.error(e); setSaveError("Errore salvataggio Step 3.");
    } finally { setSaving(false); }
  };

  // Pagina 0: Benvenuto con Norma AI (hero)
  if (showIntro) {
    return (
      <div className="min-h-[80vh] bg-gradient-to-b from-sky-50 via-white to-blue-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-10 md:py-16">
          <div className="flex flex-col md:flex-row items-stretch gap-10">

            {/* Colonna sinistra: testo + CTA */}
            <div className="w-full md:w-1/2 flex flex-col justify-center space-y-6">
              {/* Logo + pillola */}
              <div className="flex items-center gap-3">
                <img
                  src="/logonorma.png"
                  alt="Norma HACCP"
                  className="h-12 md:h-14 w-auto drop-shadow-sm"
                />
                <span className="inline-flex items-center gap-1 rounded-full bg-white/80 px-3 py-1 text-xs font-medium text-blue-700 border border-blue-100">
                  <span className="text-[10px]">●</span>
                  Configurazione guidata intelligente
                </span>
              </div>

              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
                  ✨ Benvenuto al nostro primo incontro!
                </h1>
                <p className="text-sm md:text-base text-slate-700 leading-relaxed">
                  Ciao, sono <span className="font-semibold">Norma AI</span>, il tuo assistente intelligente
                  basato su <span className="font-semibold">AI Evolution</span>, progettato per imparare dal tuo business
                  e attivare automaticamente le funzioni più utili per il tuo piano HACCP.
                </p>
              </div>

              <p className="text-sm md:text-base text-slate-700 leading-relaxed">
                In pochi minuti ti farò alcune domande mirate: le tue risposte mi permetteranno di configurare il sistema
                in modo preciso, evitando errori e garantendo che il tuo piano HACCP, le Liste di Controllo, le Checklist,
                le funzioni e i registri siano solidi fin dal primo momento.
              </p>

              {/* Box "Non hai tempo?" */}
              <div className="bg-amber-50/90 border border-amber-200 rounded-2xl p-4 flex gap-3">
                <div className="mt-1 text-amber-500">⏱️</div>
                <div>
                  <div className="font-semibold text-amber-800 mb-1">
                    Non hai tempo adesso?
                  </div>
                  <p className="text-xs md:text-sm text-amber-900">
                    Nessun problema. Puoi saltare la configurazione guidata e accedere alla{" "}
                    <span className="font-semibold">Modalità Test</span>, dove potrai esplorare il sistema
                    utilizzando dati dimostrativi già pronti.
                  </p>
                </div>
              </div>

              <div className="space-y-2 text-xs md:text-sm text-slate-600">
                <p>👣 La configurazione è composta da pochi step:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Ragione sociale e contatti principali</li>
                  <li>Unità operativa e tipologia di servizio</li>
                  <li>Prodotti e processi di lavorazione</li>
                  <li>Igiene, strutture, ruoli e servizi esterni</li>
                </ul>
              </div>

              {/* Pulsanti azione */}
              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowIntro(false)}
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm"
                >
                  Inizia la configurazione guidata
                </button>

                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await fetch("/api/autoconfig/complete.php", {
                        method: "POST",
                        credentials: "include",
                      });
                    } catch (e) {
                      console.error("Errore complete.php", e);
                    }
                    window.location.href = "/";
                  }}
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-blue-300 text-blue-700 text-sm font-semibold bg-white/70 hover:bg-blue-50"
                >
                  Salta – usa la Modalità Test
                </button>
              </div>

              <p className="text-[11px] text-slate-500">
                Scegli tu da dove iniziare. Io sono qui per guidarti.
              </p>
            </div>

            {/* Colonna destra: anteprima wizard / card decorativa */}
            <div className="w-full md:w-1/2">
              <div className="relative h-full">
                <div className="absolute -inset-4 bg-gradient-to-tr from-blue-100 via-sky-50 to-emerald-50 rounded-3xl blur-2xl opacity-70" />
                <div className="relative bg-white/90 border border-slate-100 rounded-3xl p-5 shadow-lg h-full flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Configurazione guidata Norma
                    </div>
                    <span className="text-[11px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                      Durata stimata: 8–10 min
                    </span>
                  </div>

                  <div className="flex-1 flex flex-col md:flex-row gap-4">
                    {/* Mini rail step */}
                    <div className="md:w-1/2">
                      <div className="text-xs font-semibold text-slate-500 mb-2">
                        Step previsti
                      </div>
                      <ul className="space-y-1.5">
                        {STEP_META.map((s) => (
                          <li
                            key={s.id}
                            className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2 text-xs"
                          >
                            <div className="flex items-center gap-2">
                              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[11px] text-white font-semibold">
                                {String(s.id)}
                              </span>
                              <span className="text-slate-700">{s.label}</span>
                            </div>
                            <span className="text-[10px] text-slate-500">
                              {s.minutes} min
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Mini anteprima form */}
                    <div className="md:w-1/2 mt-2 md:mt-0">
                      <div className="text-xs font-semibold text-slate-500 mb-2">
                        Esempio di domande
                      </div>
                      <div className="space-y-3 text-[11px]">
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/80 px-3 py-2">
                          <div className="font-semibold text-slate-700 mb-1">
                            Gestione Allergeni
                          </div>
                          <p className="text-slate-600">
                            La tua attività gestisce alimenti con allergeni
                            critici (gluten-free, lattosio, frutta a guscio...)?
                          </p>
                          <div className="mt-2 flex gap-2">
                            <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold">
                              Sì
                            </span>
                            <span className="px-2 py-1 rounded-full bg-rose-100 text-rose-700 text-[10px] font-semibold">
                              No
                            </span>
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/80 px-3 py-2">
                          <div className="font-semibold text-slate-700 mb-1">
                            Tipologia di servizio
                          </div>
                          <p className="text-slate-600">
                            Seleziona come servi i tuoi clienti: tavolo, banco,
                            asporto, delivery...
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                    <span>Le tue risposte genereranno automaticamente il piano HACCP.</span>
                    <span className="hidden md:inline">
                      Nessuna risposta viene persa: puoi riprendere da dove hai lasciato.
                    </span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-black">
          Configura la tua Piattaforma{" "}
          <span className="text-blue-600">Norma®</span>{" "}
          <span className="text-green-600">Haccp</span>
        </h1>
        <p className="text-gray-700 mt-2">
          Segui i passaggi successivi per configurare la tua piattaforma e ottimizzare la gestione del tuo punto ristoro: riduci i rischi alimentari, azzera le sanzioni dei NAS e degli ispettori sanitari ed elimina gli sprechi di produzione!
        </p>
        <div className="mt-3">
          <button
            type="button"
            onClick={() => { window.location.href = "/"; }}
            className="px-3 py-2 rounded-lg border hover:bg-gray-50"
            title="Torna alla dashboard e riprendi più tardi"
          >
            Esci e continua dopo
          </button>
        </div>
        {bootError && <div className="mt-2 text-red-600">{bootError}</div>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[250px,1fr] gap-6">
        {/* Rail sinistro */}
        <aside className="bg-white border border-gray-200 rounded-2xl p-3 h-max sticky top-4">
          <ul className="space-y-1">
            {STEP_META.map(m => {
              const isActive = step === m.id;
              const isDone = (done.has(m.id) && m.id < step) || (done.has(m.id) && m.id === step && lastStep >= m.id);
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => setStep(m.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg border flex items-center justify-between
                      ${isActive ? "bg-black text-white border-black" : "bg-white hover:bg-gray-50"}`}
                    title={`${m.label} [${m.minutes} min]`}
                  >
                    <span>{m.label}</span>
                    <span className={`text-xs ${isDone ? "text-emerald-600" : "text-gray-400"}`}>
                      {isDone ? "✓" : `[${m.minutes}m]`}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Contenuto step */}
        <main className="space-y-6">
          {/* STEP 1 */}
          {step === 1 && (
            <SectionCard title="Ragione Sociale (1 minuto)">
              <div className="mb-4">
                <Label>Sei il proprietario o un operatore?</Label>
                <div className="flex gap-2 mt-2">
                  <TogglePill active={utenteRuolo === "proprietario"} onClick={() => setUtenteRuolo("proprietario")}>Proprietario</TogglePill>
                  <TogglePill active={utenteRuolo === "operatore"} onClick={() => setUtenteRuolo("operatore")}>Operatore</TogglePill>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Ragione Sociale *</Label><input className="mt-1 w-full border rounded p-2" value={ragioneSociale} onChange={e => setRagioneSociale(e.target.value)} /></div>
                <div><Label>Partita IVA *</Label><input className="mt-1 w-full border rounded p-2" value={piva} onChange={e => setPiva(e.target.value)} /></div>
                <div><Label>Indirizzo *</Label><input className="mt-1 w-full border rounded p-2" value={indirizzoSede} onChange={e => setIndirizzoSede(e.target.value)} /></div>
                <div><Label>CAP *</Label><input className="mt-1 w-full border rounded p-2" value={capSede} onChange={e => setCapSede(e.target.value)} /></div>
                <div><Label>Comune *</Label><input className="mt-1 w-full border rounded p-2" value={comuneSede} onChange={e => setComuneSede(e.target.value)} /></div>
                <div><Label>Provincia *</Label><input className="mt-1 w-full border rounded p-2" value={provinciaSede} onChange={e => setProvinciaSede(e.target.value)} /></div>
                <div><Label>Codice Univoco</Label><input className="mt-1 w-full border rounded p-2" value={codiceUnivoco} onChange={e => setCodiceUnivoco(e.target.value)} /></div>
              </div>

              <div className="mt-4 grid md:grid-cols-3 gap-4">
                <div className="md:col-span-3"><Label>Referente Società / Amministratore *</Label></div>
                <div className="md:col-span-2"><input className="mt-1 w-full border rounded p-2" placeholder="Nome e Cognome" value={refNomeCognome} onChange={e => setRefNomeCognome(e.target.value)} /></div>
                <div><input className="mt-1 w-full border rounded p-2" placeholder="Cellulare (valido)" value={refCell} onChange={e => setRefCell(e.target.value)} /></div>
                <div className="md:col-span-3"><input className="mt-1 w-full border rounded p-2" placeholder="Email (valida)" value={refEmail} onChange={e => setRefEmail(e.target.value)} /></div>
              </div>

              {utenteRuolo === "operatore" && (
                <div className="mt-6">
                  <h4 className="font-semibold mb-2">Dati Operatore (obbligatori)</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <input className="mt-1 w-full border rounded p-2" placeholder="Nome" value={opNome} onChange={e => setOpNome(e.target.value)} />
                    <input className="mt-1 w-full border rounded p-2" placeholder="Cognome" value={opCognome} onChange={e => setOpCognome(e.target.value)} />
                    <input className="mt-1 w-full border rounded p-2" placeholder="Ruolo in Azienda" value={opRuolo} onChange={e => setOpRuolo(e.target.value)} />
                    <input className="mt-1 w-full border rounded p-2" placeholder="Codice Fiscale" value={opCF} onChange={e => setOpCF(e.target.value)} />
                    <input className="mt-1 w-full border rounded p-2" placeholder="Cellulare (valido)" value={opCell} onChange={e => setOpCell(e.target.value)} />
                    <input className="mt-1 w-full border rounded p-2" placeholder="Email (valida)" value={opEmail} onChange={e => setOpEmail(e.target.value)} />
                  </div>
                </div>
              )}

              <div className="mt-6 space-y-2">
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1" checked={consensoPrivacy} onChange={e => setConsensoPrivacy(e.target.checked)} />
                  <span className="text-sm">Dichiaro di aver letto e accettato la Privacy Policy, incluse le notifiche essenziali.</span>
                </label>
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1" checked={consensoMarketing} onChange={e => setConsensoMarketing(e.target.checked)} />
                  <span className="text-sm">Acconsento a ricevere comunicazioni promozionali e newsletter da NORMA®.</span>
                </label>
              </div>

              {saveError && <div className="text-red-600 mt-3">{saveError}</div>}
              <div className="mt-4">
                <button onClick={handleNext1} className={`px-4 py-2 rounded text-white ${!saving ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-wait"}`} disabled={saving}>
                  {saving ? "Salvataggio…" : "Avanti"}
                </button>
              </div>
            </SectionCard>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <SectionCard title="Unità Operativa (3 minuti)">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Nome Unità Operativa</Label><input className="mt-1 w-full border rounded p-2" value={uoNome} onChange={e => setUoNome(e.target.value)} placeholder="Es: Trattoria dell’Orologio" /></div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={uoCopiaDaAzienda} onChange={e => setUoCopiaDaAzienda(e.target.checked)} />
                    <span className="text-sm">Usa dati RS se simili</span>
                  </label>
                </div>
                <div><Label>Indirizzo</Label><input className="mt-1 w-full border rounded p-2" value={uoIndirizzo} onChange={e => setUoIndirizzo(e.target.value)} /></div>
                <div><Label>CAP</Label><input className="mt-1 w-full border rounded p-2" value={uoCap} onChange={e => setUoCap(e.target.value)} /></div>
                <div><Label>Città</Label><input className="mt-1 w-full border rounded p-2" value={uoCitta} onChange={e => setUoCitta(e.target.value)} /></div>
                <div><Label>Paese</Label><input className="mt-1 w-full border rounded p-2" value={uoPaese} onChange={e => setUoPaese(e.target.value)} /></div>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mt-4">
                <div><Label>N. addetti manipolazione</Label><input className="mt-1 w-full border rounded p-2" type="number" value={uoAddetti} onChange={e => setUoAddetti(e.target.value)} placeholder="Es: 15" /></div>
                <div><Label>MQ produzione</Label><input className="mt-1 w-full border rounded p-2" type="number" value={uoMQProduzione} onChange={e => setUoMQProduzione(e.target.value)} /></div>
                <div><Label>MQ somministrazione</Label><input className="mt-1 w-full border rounded p-2" type="number" value={uoMQSomministrazione} onChange={e => setUoMQSomministrazione(e.target.value)} /></div>
              </div>

              <div className="mt-4">
                <Label>Tipi di servizio</Label>
                <Helper>Produzione | al Tavolo | Asporto/Delivery | al Banco | Catering | Freddo | Caldo | Misto</Helper>
                <CheckList items={SERVIZI_OPTS} values={uoServizi} setValues={setUoServizi} />
              </div>

              <div className="mt-4">
                <Label>Aree di Produzione</Label>
                <CheckList items={AREE_PRODUZIONE} values={uoAreeProd} setValues={setUoAreeProd} />
              </div>

              <div className="mt-4">
                <Label>Aree di Servizio</Label>
                <CheckList items={AREE_SERVIZIO} values={uoAreeServ} setValues={setUoAreeServ} />
              </div>

              <div className="mt-4 grid md:grid-cols-3 gap-4">
                <div><Label>Direttore / Resp. Autocontrollo</Label><input className="mt-1 w-full border rounded p-2" value={respDirettore} onChange={e => setRespDirettore(e.target.value)} /></div>
                <div><Label>Resp. applicazione</Label><input className="mt-1 w-full border rounded p-2" value={respApplicazione} onChange={e => setRespApplicazione(e.target.value)} /></div>
                <div><Label>Resp. cucina</Label><input className="mt-1 w-full border rounded p-2" value={respCucina} onChange={e => setRespCucina(e.target.value)} /></div>
              </div>

              {saveError && <div className="text-red-600 mt-3">{saveError}</div>}
              <div className="mt-4 flex gap-2">
                <button onClick={() => setStep(1)} className="px-3 py-2 rounded border">Indietro</button>
                <button onClick={handleNext2} className={`px-4 py-2 rounded text-white ${!saving ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-wait"}`} disabled={saving}>
                  {saving ? "Salvataggio…" : "Avanti"}
                </button>
              </div>
            </SectionCard>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <SectionCard title="Prodotti Gestiti (3 minuti)">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Categorie Alimenti ad Alto Rischio</Label><CheckList items={CAT_ALTO_RISCHIO} values={catAltoRischio} setValues={setCatAltoRischio} /></div>
                <div><Label>Stato di Conservazione Termica</Label><CheckList items={STATI_CONSERVAZIONE} values={statiConservazione} setValues={setStatiConservazione} /></div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div><Label>Produzioni speciali e Trattamento Termico</Label><CheckList items={PROD_SPEC} values={produzioniSpeciali} setValues={setProduzioniSpeciali} /></div>
                <div><Label>Produzioni ad Alto Pericolo</Label><CheckList items={PROD_ALTO_PERICOLO} values={prodAltoPericolo} setValues={setProdAltoPericolo} /></div>
              </div>

              {saveError && <div className="text-red-600 mt-3">{saveError}</div>}
              <div className="mt-4 flex gap-2">
                <button onClick={() => setStep(2)} className="px-3 py-2 rounded border">Indietro</button>
                <button onClick={handleNext3} className={`px-4 py-2 rounded text-white ${!saving ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-wait"}`} disabled={saving}>
                  {saving ? "Salvataggio…" : "Avanti a Processi"}
                </button>
              </div>
            </SectionCard>
          )}

          {/* STEP 8: Processi di lavorazione */}
          {step === 8 && (
            <SectionCard title="Processi di lavorazione (3 minuti)">
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-2">Gestione Allergeni (comunicazione al cliente)</h4>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={allergeniGestioneOn} onChange={e => setAllergeniGestioneOn(e.target.checked)} />
                      <span className="text-sm">Gestione Allergeni attiva</span>
                    </label>
                    <div className="grid md:grid-cols-2 gap-3">
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={menuStampatoDigitale} onChange={e => setMenuStampatoDigitale(e.target.checked)} />
                        <span className="text-sm">Menù stampato/digitale (Generatore Menù Allergeni)</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={registroIngredientiCartaceo} onChange={e => setRegistroIngredientiCartaceo(e.target.checked)} />
                        <span className="text-sm">Registro ingredienti cartaceo (Stampa Registro Completo)</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={venditeSfusePreincartate} onChange={e => setVenditeSfusePreincartate(e.target.checked)} />
                        <span className="text-sm">Alimenti venduti sfusi/preincartati</span>
                      </label>
                    </div>
                    <div className="mt-2">
                      <Label>Indicazione “Può contenere tracce di…”</Label>
                      <div className="flex gap-2 mt-1">
                        <TogglePill active={avvisoTracce === true} onClick={() => setAvvisoTracce(true)}>Sì</TogglePill>
                        <TogglePill active={avvisoTracce === false} onClick={() => setAvvisoTracce(false)}>No</TogglePill>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Gestione del Rischio in Cucina (HACCP)</h4>
                  <BoolButtons label="Area/piano dedicato per allergici/celiaci (gluten-free)?" value={areaDedicataGF} onChange={setAreaDedicataGF} />
                  <BoolButtons label="Attrezzature dedicate per preparazioni free-from?" value={attrezzatureDedicate} onChange={setAttrezzatureDedicate} />
                  <BoolButtons label="Gestione ingredienti allergenici in magazzino attiva?" value={gestioneIngrAllergenici} onChange={setGestioneIngrAllergenici} />
                  <BoolButtons label="Formazione specifica su allergeni (oltre HACCP base)?" value={formazioneSpecAllergeni} onChange={setFormazioneSpecAllergeni} />
                </div>

                <div>
                  <h4 className="font-semibold mb-2">❄️ Gestione della Congelazione</h4>
                  <BoolButtons label="Si congelano alimenti internamente?" value={congelaInternamente} onChange={setCongelaInternamente} />
                  {congelaInternamente === true && (
                    <>
                      <BoolButtons label="Etichette di tracciabilità utilizzate?" value={etichetteTracciabilita} onChange={setEtichetteTracciabilita} />
                      <div className="mt-2">
                        <Label>Destinazione dell'alimento congelato</Label>
                        <div className="flex gap-2 mt-1">
                          <TogglePill active={destinazioneCongelato === "somministrazione"} onClick={() => setDestinazioneCongelato("somministrazione")}>Somministrazione</TogglePill>
                          <TogglePill active={destinazioneCongelato === "semilavorato"} onClick={() => setDestinazioneCongelato("semilavorato")}>Semi-lavorato</TogglePill>
                        </div>
                      </div>
                    </>
                  )}
                  {congelaInternamente === false && (
                    <BoolButtons label="Si utilizzano alimenti già congelati/surgelati all'origine?" value={usaCongelatiOrigine} onChange={setUsaCongelatiOrigine} />
                  )}
                </div>

                <div>
                  <h4 className="font-semibold mb-2">🍝 Semilavorati interni</h4>
                  <BoolButtons label="Vengono prodotti semilavorati interni?" value={semiList.length > 0} onChange={(v) => { if (!v) setSemiList([]); }} />
                  <div className="mt-2">
                    <CheckList items={SEMI_OPTS} values={semiList} setValues={setSemiList} />
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">💧 Sistemi di Filtrazione dell'Acqua</h4>
                  <BoolButtons label="Sono presenti macchine di filtrazione/trattamento?" value={filtriPresenti} onChange={setFiltriPresenti} />
                  {filtriPresenti === true && (
                    <div className="mt-2">
                      <Label>Aree collegate</Label>
                      <CheckList items={FILTRI_OPTS} values={filtriAree} setValues={setFiltriAree} />
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Descrizione attività</h4>
                  <label className="flex items-center gap-2 mb-2">
                    <input type="checkbox" checked={usaAI} onChange={e => setUsaAI(e.target.checked)} />
                    <span className="text-sm">Usa Norma.ai (consuma crediti) invece del campo libero</span>
                  </label>
                  {!usaAI && (
                    <textarea className="w-full min-h-[80px] border rounded p-2" placeholder="Descrivi cosa prepari e come lo servi..." value={descrizioneLibera} onChange={e => setDescrizioneLibera(e.target.value)} />
                  )}
                  <input className="mt-2 w-full border rounded p-2" placeholder="(Opzionale) Profilo attività suggerito" value={profiloSuggerito} onChange={e => setProfiloSuggerito(e.target.value)} />
                </div>
              </div>

              {saveError && <div className="text-red-600 mt-3">{saveError}</div>}
              <div className="mt-4 flex gap-2">
                <button onClick={() => setStep(3)} className="px-3 py-2 rounded border">Indietro</button>
                <button
                  onClick={async () => {
                    if (!sessionId) return;
                    setSaving(true); setSaveError(null);
                    try {
                      const out = await saveStepApi(
                        sessionId,
                        "processi_lavorazione",
                        [
                          { key: "allergeni_gestione_on", type: "bool", value: !!allergeniGestioneOn },
                          { key: "menu_stampato_digitale", type: "bool", value: !!menuStampatoDigitale },
                          { key: "registro_ingredienti_cartaceo", type: "bool", value: !!registroIngredientiCartaceo },
                          { key: "vendite_sfuse_preincartate", type: "bool", value: !!venditeSfusePreincartate },
                          { key: "avviso_tracce", type: "bool", value: avvisoTracce === true },

                          { key: "area_dedicata_gf", type: "bool", value: areaDedicataGF === true },
                          { key: "attrezzature_dedicate", type: "bool", value: attrezzatureDedicate === true },
                          { key: "gestione_ingr_allergenici", type: "bool", value: gestioneIngrAllergenici === true },
                          { key: "formazione_specifica_allergeni", type: "bool", value: formazioneSpecAllergeni === true },

                          { key: "congela_internamente", type: "bool", value: congelaInternamente === true },
                          { key: "etichette_tracciabilita", type: "bool", value: etichetteTracciabilita === true },
                          { key: "destinazione_congelato", type: "select", value: destinazioneCongelato || "" },
                          { key: "usa_congelati_origine", type: "bool", value: usaCongelatiOrigine === true },

                          { key: "produce_semilavorati", type: "bool", value: semiList.length > 0 || false },
                          { key: "lista_semilavorati", type: "multiselect", value: semiList },

                          { key: "filtri_presenti", type: "bool", value: filtriPresenti === true },
                          { key: "filtri_aree", type: "multiselect", value: filtriAree },

                          { key: "usa_ai_descrizione", type: "bool", value: !!usaAI },
                          { key: "descrizione_libera", type: "text", value: descrizioneLibera.trim() },
                          { key: "profilo_suggerito", type: "text", value: profiloSuggerito.trim() },
                        ],
                        currentUoId ? { uo_id: currentUoId } : {}
                      );
                      setLastStep(out?.last_step || lastStep);
                      setStep(4);
                    } catch (e) {
                      console.error(e); setSaveError("Errore salvataggio Processi.");
                    } finally { setSaving(false); }
                  }}
                  className={`px-4 py-2 rounded text-white ${!saving ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-wait"}`}
                  disabled={saving}
                >
                  {saving ? "Salvataggio…" : "Avanti"}
                </button>
              </div>
            </SectionCard>
          )}

          {/* STEP 4: Igiene & Sanificazione */}
          {step === 4 && (
            <SectionCard title="Igiene e Sanificazione (PRP operativi)">
              <BoolButtons label="Separazione Vestizione/Produzione (spogliatoi separati)?" value={separazioneVestizione} onChange={setSeparazioneVestizione} />
              {saveError && <div className="text-red-600 mt-3">{saveError}</div>}
              <div className="mt-4 flex gap-2">
                <button onClick={() => setStep(8)} className="px-3 py-2 rounded border">Indietro</button>
                <button
                  onClick={async () => {
                    if (!sessionId) return;
                    setSaving(true); setSaveError(null);
                    try {
                      const out = await saveStepApi(
                        sessionId,
                        "igiene_sanificazione",
                        [{ key: "separazione_vestizione", type: "bool", value: separazioneVestizione === true }],
                        currentUoId ? { uo_id: currentUoId } : {}
                      );
                      setLastStep(out?.last_step || lastStep);
                      setStep(5);
                    } catch (e) {
                      console.error(e); setSaveError("Errore salvataggio Step 4.");
                    } finally { setSaving(false); }
                  }}
                  className={`px-4 py-2 rounded text-white ${!saving ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-wait"}`}
                  disabled={saving}
                >
                  {saving ? "Salvataggio…" : "Avanti"}
                </button>
              </div>
            </SectionCard>
          )}

          {/* STEP 5: Igiene Strutturale & Impianti */}
          {step === 5 && (
            <SectionCard title="Igiene Strutturale e Impianti (PRP di Base)">
              <div className="grid md:grid-cols-2 gap-4">
                <BoolButtons label="Rubinetteria non manuale ai lavamani?" value={rubinetteriaNonManuale} onChange={setRubinetteriaNonManuale} />
                <BoolButtons label="Illuminazione protetta/antisfondamento nelle aree di lavoro?" value={illuminazioneProtetta} onChange={setIlluminazioneProtetta} />
              </div>
              <div className="mt-2">
                <Label>Controllo Infestanti</Label>
                <div className="flex gap-2 mt-2">
                  <TogglePill active={controlloInfestanti === "attivo"} onClick={() => setControlloInfestanti("attivo")}>Attivo (ditta esterna)</TogglePill>
                  <TogglePill active={controlloInfestanti === "passivo"} onClick={() => setControlloInfestanti("passivo")}>Passivo (monitoraggio interno)</TogglePill>
                </div>
              </div>
              <div className="mt-2">
                <BoolButtons label="Sigillatura strutturale (reti/spazzole a porte/finestre) presente?" value={sigillaturaStrutturale} onChange={setSigillaturaStrutturale} />
              </div>

              {saveError && <div className="text-red-600 mt-3">{saveError}</div>}
              <div className="mt-4 flex gap-2">
                <button onClick={() => setStep(4)} className="px-3 py-2 rounded border">Indietro</button>
                <button
                  onClick={async () => {
                    if (!sessionId) return;
                    setSaving(true); setSaveError(null);
                    try {
                      const out = await saveStepApi(
                        sessionId,
                        "igiene_strutturale_impianti",
                        [
                          { key: "rubinetteria_non_manuale", type: "bool", value: rubinetteriaNonManuale === true },
                          { key: "illuminazione_protetta", type: "bool", value: illuminazioneProtetta === true },
                          { key: "controllo_infestanti", type: "select", value: controlloInfestanti },
                          { key: "sigillatura_strutturale", type: "bool", value: sigillaturaStrutturale === true },
                        ],
                        currentUoId ? { uo_id: currentUoId } : {}
                      );
                      setLastStep(out?.last_step || lastStep);
                      setStep(6);
                    } catch (e) {
                      console.error(e); setSaveError("Errore salvataggio Step 5.");
                    } finally { setSaving(false); }
                  }}
                  className={`px-4 py-2 rounded text-white ${!saving ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-wait"}`}
                  disabled={saving}
                >
                  {saving ? "Salvataggio…" : "Avanti"}
                </button>
              </div>
            </SectionCard>
          )}
          {/* STEP 6: Ruoli, responsabilità e Formazione */}
          {step === 6 && (
            <SectionCard title="Ruoli, responsabilità e Formazione">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Responsabile HACCP - Nome</Label><input className="mt-1 w-full border rounded p-2" value={haccpRespNome} onChange={e => setHaccpRespNome(e.target.value)} /></div>
                <div><Label>Responsabile HACCP - Cognome</Label><input className="mt-1 w-full border rounded p-2" value={haccpRespCognome} onChange={e => setHaccpRespCognome(e.target.value)} /></div>
                <div><Label>Ruolo in Azienda</Label><input className="mt-1 w-full border rounded p-2" value={haccpRespRuolo} onChange={e => setHaccpRespRuolo(e.target.value)} /></div>
                <div><Label>Cellulare</Label><input className="mt-1 w-full border rounded p-2" value={haccpRespCell} onChange={e => setHaccpRespCell(e.target.value)} /></div>
                <div><Label>Email</Label><input className="mt-1 w-full border rounded p-2" value={haccpRespEmail} onChange={e => setHaccpRespEmail(e.target.value)} /></div>
              </div>
              <div className="mt-3">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={haccpRespIsReferente} onChange={e => setHaccpRespIsReferente(e.target.checked)} />
                  <span className="text-sm">Il Responsabile è anche Referente Società/Amministratore</span>
                </label>
              </div>
              <div className="mt-4 grid md:grid-cols-3 gap-4">
                <BoolButtons label="Il personale è formato (ultimi 3 anni)?" value={formazionePersonaleOk} onChange={setFormazionePersonaleOk} />
                <BoolButtons label="Si utilizza acqua di pozzo?" value={usaAcquaPozzo} onChange={setUsaAcquaPozzo} />
                <div>
                  <Label>Numero Autorizzazione ULSS/ATS (se acqua di pozzo)</Label>
                  <input className="mt-1 w-full border rounded p-2" value={ulssAutorizzazione} onChange={e => setUlssAutorizzazione(e.target.value)} />
                </div>
              </div>

              {saveError && <div className="text-red-600 mt-3">{saveError}</div>}
              <div className="mt-4 flex gap-2">
                <button onClick={() => setStep(5)} className="px-3 py-2 rounded border">Indietro</button>
                <button
                  onClick={async () => {
                    if (!sessionId) return;
                    setSaving(true); setSaveError(null);
                    try {
                      const out = await saveStepApi(
                        sessionId,
                        "ruoli_responsabilita_formazione",
                        [
                          { key: "haccp_resp_nome", type: "text", value: haccpRespNome.trim() },
                          { key: "haccp_resp_cognome", type: "text", value: haccpRespCognome.trim() },
                          { key: "haccp_resp_ruolo", type: "text", value: haccpRespRuolo.trim() },
                          { key: "haccp_resp_cell", type: "text", value: haccpRespCell.trim() },
                          { key: "haccp_resp_email", type: "text", value: haccpRespEmail.trim() },
                          { key: "haccp_resp_is_referente", type: "bool", value: !!haccpRespIsReferente },
                          { key: "formazione_personale_ok", type: "bool", value: formazionePersonaleOk === true },
                          { key: "usa_acqua_pozzo", type: "bool", value: usaAcquaPozzo === true },
                          { key: "ulss_autorizzazione", type: "text", value: ulssAutorizzazione.trim() },
                        ]
                      );
                      setLastStep(out?.last_step || lastStep);
                      setStep(7);
                    } catch (e) {
                      console.error(e); setSaveError("Errore salvataggio Step 6.");
                    } finally { setSaving(false); }
                  }}
                  className={`px-4 py-2 rounded text-white ${!saving ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-wait"}`}
                  disabled={saving}
                >
                  {saving ? "Salvataggio…" : "Avanti"}
                </button>
              </div>
            </SectionCard>
          )}

          {/* STEP 7: Servizi Esterni */}
          {step === 7 && (
            <SectionCard title="Servizi Esterni">
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-2">Ditta per la Disinfestazione</h4>
                  <label className="flex items-center gap-2 mb-3">
                    <input type="checkbox" checked={hasDittaInf} onChange={e => setHasDittaInf(e.target.checked)} />
                    <span className="text-sm">Contratto attivo con azienda specializzata</span>
                  </label>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div><Label>Società</Label><input className="mt-1 w-full border rounded p-2" value={infSocieta} onChange={e => setInfSocieta(e.target.value)} /></div>
                    <div><Label>Sede</Label><input className="mt-1 w-full border rounded p-2" value={infSede} onChange={e => setInfSede(e.target.value)} /></div>
                    <div><Label>Partita IVA</Label><input className="mt-1 w-full border rounded p-2" value={infPiva} onChange={e => setInfPiva(e.target.value)} /></div>
                    <div><Label>Telefono aziendale</Label><input className="mt-1 w-full border rounded p-2" value={infTel} onChange={e => setInfTel(e.target.value)} /></div>
                    <div><Label>Email</Label><input className="mt-1 w-full border rounded p-2" value={infEmail} onChange={e => setInfEmail(e.target.value)} /></div>
                    <div><Label>Referente - Nome</Label><input className="mt-1 w-full border rounded p-2" value={infRefNome} onChange={e => setInfRefNome(e.target.value)} /></div>
                    <div><Label>Referente - Cognome</Label><input className="mt-1 w-full border rounded p-2" value={infRefCognome} onChange={e => setInfRefCognome(e.target.value)} /></div>
                    <div><Label>Referente - Cellulare</Label><input className="mt-1 w-full border rounded p-2" value={infRefCell} onChange={e => setInfRefCell(e.target.value)} /></div>
                    <div className="md:col-span-2"><Label>N. di telefono pronto intervento</Label><input className="mt-1 w-full border rounded p-2" value={infTelPronto} onChange={e => setInfTelPronto(e.target.value)} placeholder="+39..." /></div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Ditta Olii esausti</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div><Label>Società</Label><input className="mt-1 w-full border rounded p-2" value={olSocieta} onChange={e => setOlSocieta(e.target.value)} /></div>
                    <div><Label>Sede</Label><input className="mt-1 w-full border rounded p-2" value={olSede} onChange={e => setOlSede(e.target.value)} /></div>
                    <div><Label>Partita IVA</Label><input className="mt-1 w-full border rounded p-2" value={olPiva} onChange={e => setOlPiva(e.target.value)} /></div>
                    <div><Label>Telefono aziendale</Label><input className="mt-1 w-full border rounded p-2" value={olTel} onChange={e => setOlTel(e.target.value)} /></div>
                    <div><Label>Email</Label><input className="mt-1 w-full border rounded p-2" value={olEmail} onChange={e => setOlEmail(e.target.value)} /></div>
                    <div><Label>Referente - Nome</Label><input className="mt-1 w-full border rounded p-2" value={olRefNome} onChange={e => setOlRefNome(e.target.value)} /></div>
                    <div><Label>Referente - Cognome</Label><input className="mt-1 w-full border rounded p-2" value={olRefCognome} onChange={e => setOlRefCognome(e.target.value)} /></div>
                    <div><Label>Referente - Cellulare</Label><input className="mt-1 w-full border rounded p-2" value={olRefCell} onChange={e => setOlRefCell(e.target.value)} /></div>
                    <div className="md:col-span-2"><Label>N. di telefono pronto intervento</Label><input className="mt-1 w-full border rounded p-2" value={olTelPronto} onChange={e => setOlTelPronto(e.target.value)} placeholder="+39..." /></div>
                  </div>
                </div>
              </div>

              {saveError && <div className="text-red-600 mt-3">{saveError}</div>}
              <div className="mt-4 flex gap-2">
                <button onClick={() => setStep(6)} className="px-3 py-2 rounded border">Indietro</button>
                <button
                  onClick={async () => {
                    if (!sessionId) return;
                    setSaving(true); setSaveError(null);
                    try {
                      const out = await saveStepApi(
                        sessionId,
                        "servizi_esterni",
                        [
                          { key: "has_ditta_infestanti", type: "bool", value: !!hasDittaInf },
                          { key: "inf_societa", type: "text", value: infSocieta.trim() },
                          { key: "inf_sede", type: "text", value: infSede.trim() },
                          { key: "inf_piva", type: "text", value: infPiva.trim() },
                          { key: "inf_tel", type: "text", value: infTel.trim() },
                          { key: "inf_email", type: "text", value: infEmail.trim() },
                          { key: "inf_ref_nome", type: "text", value: infRefNome.trim() },
                          { key: "inf_ref_cognome", type: "text", value: infRefCognome.trim() },
                          { key: "inf_ref_cell", type: "text", value: infRefCell.trim() },
                          { key: "inf_tel_pronto", type: "text", value: infTelPronto.trim() },

                          { key: "ol_societa", type: "text", value: olSocieta.trim() },
                          { key: "ol_sede", type: "text", value: olSede.trim() },
                          { key: "ol_piva", type: "text", value: olPiva.trim() },
                          { key: "ol_tel", type: "text", value: olTel.trim() },
                          { key: "ol_email", type: "text", value: olEmail.trim() },
                          { key: "ol_ref_nome", type: "text", value: olRefNome.trim() },
                          { key: "ol_ref_cognome", type: "text", value: olRefCognome.trim() },
                          { key: "ol_ref_cell", type: "text", value: olRefCell.trim() },
                          { key: "ol_tel_pronto", type: "text", value: olTelPronto.trim() },
                        ],
                        currentUoId ? { uo_id: currentUoId } : {}
                      );
                      setLastStep(out?.last_step || lastStep);

                      // ✅ chiudi il configuratore e vai in dashboard
                      window.location.href = "/";
                    } catch (e) {
                      console.error(e); setSaveError("Errore salvataggio Step 7.");
                    } finally { setSaving(false); }
                  }}
                  className={`px-4 py-2 rounded text-white ${!saving ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-wait"}`}
                  disabled={saving}
                >
                  {saving ? "Salvataggio…" : "Conferma & Fine"}
                </button>
              </div>
            </SectionCard>
          )}
        </main>
      </div>
    </div>
  );
}
